import { useEffect } from 'react';

import { Box } from '#/components/Box/Box';
import { EventDetailsList } from '#/components/DetailsList/Event/EventDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useCurrentUserIsStaff } from '#/store/hooks/useCurrentUserIsStaff';
import { useIsExternalUrl } from '#/hooks/useIsExternalUrl';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewExpiredTag } from '#/modules/listing/components/ListingView/ExpiredTag/ListingViewExpiredTag';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import type { ApiEvent } from '#/modules/listing/events/api/types/ApiEvent';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { showModal } from '#/zustand-stores/modalStore';
import { dismissToast, showToast } from '#/zustand-stores/toastsStore';

type Props = {
  event: ApiEvent;
};

export function ExpiredEventPage({ event }: Props) {
  const { i18n, locale } = useI18n();
  const { isExternalUrl } = useIsExternalUrl();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: event.id,
  });

  const { user } = useUser();
  const isStaff = useCurrentUserIsStaff();
  const isOrgOwner = isUserOrgOwner(user, event.org?.id);

  useEffect(() => {
    trackViewListing({
      status: 'expired',
      hasAts: false,
      listingType: event.type,
      listingId: event.id,
      listingName: event.name,
      orgId: event.org?.id,
      orgName: event.org?.name,
      orgType: event.org?.orgType,
    });
  }, [event]);

  useEffect(() => {
    if (isStaff) return;

    const toastId = showToast({
      headline: i18n('This listing has expired'),
      body: i18n(
        'This listing has expired and is no longer accepting applications.',
      ),
      dismissible: true,
      variant: 'warning',
    });

    return () => {
      dismissToast(toastId);
    };
  }, [i18n, isStaff]);

  const orgLogo = event.org?.logo ?? null;
  const orgName = event.org?.name ?? null;
  const orgUrl = event.org?.url[locale] ?? null;
  const orgType = event.org?.orgType ?? null;
  const expiredAt = Date.parse(event.expires)
    ? new Date(event.expires)
    : undefined;

  return (
    <>
      {isOrgOwner && event.org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this event')}
          tools={
            <ListingViewAdminTools
              listingId={event.id}
              listingType="EVENT"
              orgId={event.org?.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === event.org?.id)?.canPostEvents,
              )}
              hasAts={false}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={event.address}
            listingName={event.name}
            listingType="EVENT"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={event.remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={event.firstPublished}
            listingType={event.type}
            showScrollToApplyButton={false}
            applied={false}
            listingViewStatus={ListingViewStatus.EXPIRED}
          />
          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewExpiredTag expiredAt={expiredAt} isStaff={isStaff} />

          <ListingViewHeader
            address={event.address}
            isPostedAnonymously={false}
            listingId={event.id}
            listingName={event.name}
            listingType={event.type}
            listingFirstPublished={event.firstPublished}
            orgName={orgName}
            orgType={orgType}
            orgUrl={orgUrl}
            remoteOptions={event.remoteOptions}
            listingViewStatus={ListingViewStatus.EXPIRED}
            actionButtons={null}
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <EventDetailsList
              areasOfFocus={event.areasOfFocus}
              startDate={event.startDate}
              startTime={event.startTime}
              endDate={event.endDate}
              endTime={event.endTime}
              feeAmount={event.feeAmount}
              feeRequired={event.feeRequired}
              timezone={event.timezone}
              welcomeGroups={event.welcomeGroups}
              welcomeFamilies={event.welcomeFamilies}
              detailsWheelchairAccessible={event.detailsWheelchairAccessible}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={event.name}
            image={event.image}
            description={event.description}
            descriptionTruncated={event.descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalUrl(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: [],
                  orgId: event.org?.id,
                  listingName: event.name,
                  listingType: 'EVENT',
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this event')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: event.type,
                locale: event.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={event.type}
            address={event.address}
            directions={event.directions}
            remoteOptions={event.remoteOptions}
          />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={event.url[locale]}
              type={event.type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
